export default {
  meta: {
    title: 'Docker1s - Fast & Stable Docker Image Acceleration Service',
    description: 'Providing fast, stable, and free Docker image download acceleration service, supporting Docker Hub, gcr.io, k8s.gcr.io and other image registries with global CDN network, 10x speed boost',
    keywords: 'docker,image acceleration,docker acceleration,container,kubernetes,docker images,container technology,docker hub,gcr.io,k8s.gcr.io,docker pull,image download,container images,docker registry,image repository,CDN acceleration,global acceleration,free acceleration,docker1s,container deployment,microservices,devops,CI/CD,containerization,docker daemon,registry mirror,image proxy,container cloud,cloud native,kubernetes images,k8s images,container orchestration,docker compose,dockerfile,container image acceleration,docker image proxy,container image cache,docker network acceleration,image pull optimization,docker mirror,registry proxy,container registry,image caching,docker speed,container speed,fast docker,docker performance,kubernetes acceleration,k8s acceleration',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Docker1s',
      description: 'Fast and stable Docker image acceleration service providing free container image download acceleration',
      url: 'https://docker1s.com',
      applicationCategory: 'DeveloperApplication',
      operatingSystem: 'Linux, macOS, Windows',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      provider: {
        '@type': 'Organization',
        name: 'Docker1s'
      },
      featureList: [
        'Docker Image Acceleration',
        'Global CDN Network',
        'Multiple Registry Support',
        'Free Unlimited Usage',
        'HTTPS Secure Transfer'
      ]
    }
  },
  common: {
    login: 'Login',
    register: 'Register',
    logout: 'Logout',
    profile: 'Profile',
    settings: 'Settings',
    language: 'Language',
    darkMode: 'Dark Mode',
    lightMode: 'Light Mode',
    copy: 'Copy'
  },
  nav: {
    home: 'Home',
    docs: 'Documentation',
    status: 'Status',
    about: 'About',
    blog: 'Blog'
  },
  home: {
    title: 'One-Click Acceleration, Reshape Your Container World',
    subtitle: 'Our AI-powered container image acceleration platform provides fast, simple, and ultra-high-speed Docker image downloads.',
    quickStart: 'Get Started',
    pricing: 'View Pricing',
    stats: {
      accelerations: 'Image Accelerations',
      users: 'Active Users',
      uptime: 'Service Uptime'
    },
    demo: {
      title: 'Quick Start',
      subtitle: 'Just one command to enjoy ultra-fast container image download experience',
      tabs: {
        config: 'Accelerator Config',
        pull: 'Image Pull',
        k8s: 'K8s Deployment',
        compose: 'Compose Config'
      }
    },
    features: {
      title: 'Powerful Acceleration Features',
      subtitle: 'Transform your container world with cutting-edge technology - everything you need is here',
      items: {
        speed: {
          title: 'Smart Image Acceleration',
          desc: 'Using our advanced CDN network and intelligent caching technology to provide 10x+ acceleration for Docker image downloads, say goodbye to long waits.'
        },
        global: {
          title: 'Global Node Coverage',
          desc: 'Deploy acceleration nodes worldwide with optimal routing, enjoy ultra-fast image downloads no matter where you are.'
        },
        secure: {
          title: 'Security & Reliability',
          desc: 'HTTPS encryption, integrity verification for secure images, 99.9% high availability ensures your container deployments never fail.'
        },
        free: {
          title: 'Free Unlimited Usage',
          desc: 'Completely free image acceleration service, no registration required, supports Docker Hub, gcr.io, k8s.gcr.io and other mainstream registries.'
        },
        intelligent: {
          title: 'Intelligent Cache Optimization',
          desc: 'AI-driven intelligent caching algorithms predict and preload popular images, achieving millisecond response times that make image downloads faster than local copying.'
        },
        support: {
          title: '24/7 Technical Support',
          desc: 'Round-the-clock technical support service with professional teams monitoring service status in real-time, ensuring quick response and resolution to any issues.'
        }
      }
    }
  },
  docs: {
    title: 'Documentation',
    subtitle: 'Quick start guide for Docker1s image acceleration service',
    sections: {
      gettingStarted: 'Getting Started',
      supportedRegistries: 'Supported Registries',
      faq: 'FAQ'
    },
    content: {
      gettingStarted: {
        title: 'Getting Started',
        description: 'Docker1s is a free Docker image acceleration service that can be used without registration.',
        method1: 'Method 1: Configure Registry Mirror (Recommended)',
        configComment: 'Create or modify /etc/docker/daemon.json',
        restartComment: 'Restart Docker service',
        method2: 'Method 2: Direct Pull',
        directPullComment: 'Pull images directly using the acceleration domain'
      },
      supportedRegistries: {
        title: 'Supported Registries',
        description: 'Docker1s supports acceleration for the following mainstream image registries:',
        dockerHub: 'Official image registry with a large number of quality images',
        gcr: "Google's container image registry",
        k8s: 'Kubernetes official image registry'
      },
      kubernetes: {
        title: 'Using in Kubernetes',
        description: 'Use Docker1s to accelerate image pulling in Kubernetes clusters.',
        deploymentExample: 'Deployment Example'
      },
      dockerCompose: {
        title: 'Docker Compose',
        description: 'Use accelerated images in Docker Compose.'
      },
      faq: {
        title: 'Frequently Asked Questions',
        q1: {
          question: 'Q: Is Docker1s free?',
          answer: 'A: Yes, Docker1s is completely free and can be used without registration.'
        },
        q2: {
          question: 'Q: How effective is the acceleration?',
          answer: 'A: Depending on geographic location and network conditions, it typically provides 3-10x download speed improvement.'
        },
        q3: {
          question: 'Q: Does it support private images?',
          answer: 'A: Currently only public image acceleration is supported. Please use the original registry address for private images.'
        },
        q4: {
          question: 'Q: How to verify if the configuration is effective?',
          answer: 'A: Run the docker info command and check if the Registry Mirrors field contains our acceleration address.'
        }
      }
    }
  },
  status: {
    title: 'Service Status',
    subtitle: 'Real-time monitoring of Docker1s image acceleration service status',
    allOperational: 'All Systems Operational',
    operational: 'Operational',
    duration: 'Duration',
    sections: {
      serviceStatus: 'Service Status',
      performanceMetrics: 'Performance Metrics',
      incidentHistory: 'Incident History'
    },
    services: {
      dockerHub: {
        name: 'Docker Hub Acceleration',
        description: 'Official image registry acceleration service'
      },
      gcr: {
        name: 'gcr.io Acceleration',
        description: 'Google Container Registry acceleration'
      },
      k8sGcr: {
        name: 'k8s.gcr.io Acceleration',
        description: 'Kubernetes image registry acceleration'
      },
      cdn: {
        name: 'CDN Network',
        description: 'Global content delivery network'
      },
      quay: {
        name: 'Quay.io Acceleration',
        description: 'Red Hat Quay image registry acceleration'
      },
      monitoring: {
        name: 'Monitoring System',
        description: '7x24 real-time service monitoring'
      }
    },
    metrics: {
      responseTime: 'Response Time',
      uptime: 'Uptime',
      avgResponseTime: 'Average Response Time',
      dailyRequests: 'Daily Requests',
      globalNodes: 'Global Nodes',
      systemUptime: 'System Uptime'
    },
    changes: {
      responseTime: '↑ 15% from yesterday',
      dailyRequests: '↑ 8.2% from yesterday',
      globalNodes: '↑ 2 new nodes',
      systemUptime: '= Steady'
    },
    incidents: {
      incident1: {
        title: 'Increased latency in APAC nodes',
        description: 'Some APAC nodes experienced increased latency due to upstream network fluctuations. Service has been restored.',
        time: '2 hours ago',
        duration: '45 minutes'
      },
      incident2: {
        title: 'Scheduled maintenance completed',
        description: 'Scheduled maintenance on European nodes has been completed, further optimizing service performance.',
        time: '1 day ago',
        duration: '30 minutes'
      },
      incident3: {
        title: 'New Southeast Asia nodes added',
        description: 'Added 2 new acceleration nodes in Singapore and Indonesia to further improve access speed in Southeast Asia.',
        time: '3 days ago'
      }
    }
  },
  about: {
    title: 'About Docker1s',
    subtitle: 'Committed to providing the highest quality container image acceleration service for developers worldwide',
    mission: {
      title: 'Our Mission',
      description: 'Docker1s is dedicated to solving the slow image download problem that developers worldwide face when using Docker container technology. We believe technology should make development simpler and more efficient, not a barrier. Through our global acceleration network, we enable every developer to enjoy ultra-fast container image download experiences.'
    },
    values: {
      title: 'Core Values',
      performance: {
        title: 'Ultimate Performance',
        description: 'Continuously optimizing our CDN network and caching strategies to provide users with the fastest download speeds.'
      },
      security: {
        title: 'Security & Reliability',
        description: 'Strict security measures and high-availability architecture ensure service stability and data security.'
      },
      openness: {
        title: 'Open Sharing',
        description: 'Adhering to the open source spirit, providing free image acceleration services to the global developer community.'
      },
      global: {
        title: 'Global Vision',
        description: 'Building a service network covering the globe, providing consistent high-quality experiences for developers worldwide.'
      }
    },
    achievements: {
      title: 'Our Achievements',
      totalPulls: 'Total Image Pulls',
      activeUsers: 'Active Developer Users',
      globalNodes: 'Global Acceleration Nodes',
      uptime: 'Service Uptime'
    },
    team: {
      title: 'Team Introduction',
      description: 'We are a passionate technical team with rich experience in cloud computing, CDN, and container technologies. Team members come from well-known internet companies and have been deeply involved in the design and optimization of multiple large-scale distributed systems.',
      expertise: {
        title: 'Technical Expertise',
        description: 'Deep technical accumulation in CDN, load balancing, container technology and other fields'
      },
      global: {
        title: 'Global Perspective',
        description: 'Team members are distributed across multiple countries and regions, with deep understanding of global developer needs'
      },
      innovation: {
        title: 'Continuous Innovation',
        description: 'Constantly exploring new technologies, optimizing service architecture, and improving user experience'
      }
    },
    contact: {
      title: 'Contact Us',
      support: {
        title: 'Technical Support',
        description: 'If you encounter any problems during use, please feel free to contact our technical support team.'
      },
      join: {
        title: 'Join Us',
        description: 'We are looking for excellent engineers to join our team and build a better container ecosystem together.',
        viewJobs: 'View Jobs'
      }
    }
  },
  auth: {
    login: {
      subtitle: 'Welcome back! Please sign in to your account'
    },
    register: {
      subtitle: 'Create your Docker1s account'
    },
    email: 'Email Address',
    password: 'Password',
    username: 'Username',
    confirmPassword: 'Confirm Password',
    emailPlaceholder: 'Enter your email',
    passwordPlaceholder: 'Enter your password',
    usernamePlaceholder: 'Enter your username',
    passwordMinLength: 'Enter password (at least 8 characters)',
    confirmPasswordPlaceholder: 'Enter your password again',
    rememberMe: 'Remember me',
    forgotPassword: 'Forgot password?',
    loggingIn: 'Signing in...',
    registering: 'Registering...',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    registerNow: 'Sign up now',
    loginNow: 'Sign in now',
    or: 'or',
    githubLogin: 'Sign in with GitHub',
    googleLogin: 'Sign in with Google',
    agreeTerms: 'I agree to the',
    termsOfService: 'Terms of Service',
    privacyPolicy: 'Privacy Policy',
    and: 'and',
    passwordMismatch: 'Password confirmation does not match',
    registerFailed: 'Registration failed'
  },
  footer: {
    description: 'Providing fast, stable, and free Docker image download acceleration service',
    builtWith: 'Built with ❤️ for the Docker1s community',
    sections: {
      product: 'Product',
      support: 'Support',
      company: 'Company'
    },
    links: {
      imageAcceleration: 'Image Acceleration',
      apiService: 'API Service',
      privateDeployment: 'Private Deployment',
      enterprise: 'Enterprise',
      apiDocs: 'API Documentation',
      faq: 'FAQ',
      contact: 'Contact Us',
      privacy: 'Privacy Policy'
    }
  }
}
